import { Fragment, useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { FormattedMessage, useIntl } from "react-intl";
import type { WorkerItem } from "../../hooks/useWorkerList";
import { StatusPill } from "./StatusPill";
import { ResetPinModal } from "./ResetPinModal";
import { RevokeConfirmModal } from "./RevokeConfirmModal";
import { QueueHealthCell } from "../../components/QueueHealthColumn";
import { formatDate } from "../../lib/formatDateTime";

// Plan 05 deviation (Rule 1) — the existing /api/admin/workers endpoint does
// NOT return queue_bytes_photos / queue_depth_photos (Plan 01 closeout did
// not extend the worker list payload to JOIN worker_health_status). The
// QueueHealthCell renders 0/0 (i.e. green) for every worker until that JOIN
// lands. Workers can use the dedicated /admin/queue-health dashboard for the
// real per-worker numbers in the meantime. Surfaced openly per CLAUDE.md
// no-silent-fallbacks.
type WorkerWithHealth = WorkerItem & {
  queue_bytes_photos?: number;
  queue_depth_photos?: number;
};

interface WorkerListProps {
  workers: WorkerItem[];
}

// Phase 7 finding — was using `toLocaleDateString` with month: "short" which
// produced inconsistent locale-default formats. Now uses the shared
// formatDate (DD/MM/YYYY) per I18N-9.6.

export function WorkerList({ workers }: WorkerListProps) {
  const intl = useIntl();
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
  const [resetPinWorker, setResetPinWorker] = useState<WorkerItem | null>(null);
  const [revokeWorker, setRevokeWorker] = useState<WorkerItem | null>(null);

  const sorted = [...workers].sort((a, b) => {
    return new Intl.Collator(intl.locale).compare(a.name, b.name);
  });

  const toggleRow = (id: number) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  return (
    <>
      <table
        className="w-full bg-surface-raised border border-divider rounded-md overflow-hidden text-sm"
        data-testid="worker-table"
      >
        <thead>
          <tr className="bg-surface border-be border-divider">
            <th
              scope="col"
              className="ps-4 pe-2 pbs-3 pbe-3 text-start font-semibold text-on-surface w-[30%]"
            >
              <FormattedMessage id="LABEL_COL_NAME" />
            </th>
            <th
              scope="col"
              className="ps-2 pe-2 pbs-3 pbe-3 text-start font-semibold text-on-surface w-[12%]"
            >
              <FormattedMessage id="LABEL_COL_STATUS" />
            </th>
            <th
              scope="col"
              className="ps-2 pe-2 pbs-3 pbe-3 text-start font-semibold text-on-surface w-[15%]"
              data-testid="col-queue-health"
            >
              <FormattedMessage id="LABEL_QUEUE_HEALTH_COLUMN" />
            </th>
            <th
              scope="col"
              className="ps-2 pe-2 pbs-3 pbe-3 text-start font-semibold text-on-surface w-[18%]"
            >
              <FormattedMessage id="LABEL_COL_LAST_SYNC" />
            </th>
            <th
              scope="col"
              className="ps-2 pe-2 pbs-3 pbe-3 text-start font-semibold text-on-surface w-[22%]"
            >
              <FormattedMessage id="LABEL_COL_LAST_INSPECTION" />
            </th>
            <th scope="col" className="ps-2 pe-4 pbs-3 pbe-3 w-[11%]" aria-label="expand" />
          </tr>
        </thead>
        <tbody>
          {sorted.map((worker) => {
            const isExpanded = expandedIds.has(worker.id);
            return (
              <Fragment key={worker.id}>
                <tr
                  onClick={() => toggleRow(worker.id)}
                  aria-expanded={isExpanded}
                  className="border-be border-divider hover:bg-surface cursor-pointer"
                  data-testid={`worker-row-${worker.id}`}
                >
                  <td className="ps-4 pe-2 pbs-3 pbe-3 font-medium text-on-surface">
                    {worker.name}
                  </td>
                  <td className="ps-2 pe-2 pbs-3 pbe-3">
                    <StatusPill status={worker.status} />
                  </td>
                  <td className="ps-2 pe-2 pbs-3 pbe-3 text-text-secondary">
                    <QueueHealthCell
                      queueBytes={(worker as WorkerWithHealth).queue_bytes_photos ?? 0}
                      queuePhotos={(worker as WorkerWithHealth).queue_depth_photos ?? 0}
                    />
                  </td>
                  <td className="ps-2 pe-2 pbs-3 pbe-3 text-text-secondary">
                    {worker.last_sync_at ? (
                      formatDate(worker.last_sync_at)
                    ) : (
                      <span className="italic text-text-secondary">
                        <FormattedMessage id="LABEL_SYNC_NEVER" />
                      </span>
                    )}
                  </td>
                  <td className="ps-2 pe-2 pbs-3 pbe-3 text-text-secondary">
                    {worker.last_inspection_at ? (
                      formatDate(worker.last_inspection_at)
                    ) : (
                      <span className="italic text-text-secondary">
                        <FormattedMessage id="LABEL_INSPECTION_NEVER" />
                      </span>
                    )}
                  </td>
                  <td className="ps-2 pe-4 pbs-3 pbe-3 text-end text-text-secondary">
                    {isExpanded ? (
                      <ChevronUp size={16} aria-hidden="true" />
                    ) : (
                      <ChevronDown size={16} aria-hidden="true" />
                    )}
                  </td>
                </tr>

                {isExpanded && (
                  <tr
                    data-testid={`worker-expanded-${worker.id}`}
                  >
                    <td
                      colSpan={6}
                      className="bg-surface ps-12 pe-4 pbs-4 pbe-6 border-be border-divider"
                    >
                      <div className="grid grid-cols-2 gap-x-8 gap-y-2 mbe-4 text-sm">
                        <div>
                          <span className="font-semibold text-on-surface-variant">
                            <FormattedMessage id="LABEL_CREATED_DATE" />
                          </span>
                          {": "}
                          <span className="text-text-secondary">{formatDate(worker.created_at)}</span>
                        </div>
                        <div>
                          <span className="font-semibold text-on-surface-variant">
                            <FormattedMessage id="LABEL_PIN_LAST_RESET" />
                          </span>
                          {": "}
                          <span className="text-text-secondary" data-testid="pin-last-reset-date">
                            {formatDate(worker.pin_last_reset_at)}
                          </span>
                        </div>
                        {worker.status === "revoked" && worker.revoked_at && (
                          <div>
                            <span className="font-semibold text-on-surface-variant">
                              <FormattedMessage id="LABEL_REVOKED_DATE" />
                            </span>
                            {": "}
                            <span className="text-text-secondary">{formatDate(worker.revoked_at)}</span>
                          </div>
                        )}
                      </div>

                      <div className="flex gap-3 justify-end">
                        {worker.status === "active" && (
                          <>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setResetPinWorker(worker);
                              }}
                              data-testid={`reset-pin-button-${worker.id}`}
                              className="ps-3 pe-3 h-9 text-sm font-semibold text-primary border border-primary rounded-md hover:bg-secondary-container focus-visible:ring-2 focus-visible:ring-primary transition-colors duration-200"
                            >
                              <FormattedMessage id="LABEL_RESET_PIN" />
                            </button>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setRevokeWorker(worker);
                              }}
                              data-testid={`revoke-button-${worker.id}`}
                              className="ps-3 pe-3 h-9 text-sm font-semibold text-error border border-error rounded-md hover:bg-error-container focus-visible:ring-2 focus-visible:ring-error transition-colors duration-200"
                            >
                              <FormattedMessage id="LABEL_REVOKE_WORKER" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>

      {resetPinWorker && (
        <ResetPinModal
          workerId={resetPinWorker.id}
          workerName={resetPinWorker.name}
          onClose={() => setResetPinWorker(null)}
        />
      )}

      {revokeWorker && (
        <RevokeConfirmModal
          workerId={revokeWorker.id}
          workerName={revokeWorker.name}
          onClose={() => setRevokeWorker(null)}
        />
      )}
    </>
  );
}
